import { useState, type ReactNode } from 'react'
import { Trash2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { RowActions } from '@/components/ui/row-actions'
import { Table, TableActionsCell, TableActionsHead, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { requestErrorMessage } from '@/helpers/apiClient'
import { instantLabel } from '@/lib/instantLabel'
import { useRemoveAdConnection } from '@/hooks/useAds'
import type { AdConnectionDto } from '@lib/dto/ads.dto'

interface AdConnectionListProps {
  connections: AdConnectionDto[]
  /** The connect control, at the right of the list's band. */
  action: ReactNode
}

function accountsLabel(count: number): string {
  if (count === 0) return 'No accounts'
  return count === 1 ? '1 account' : `${String(count)} accounts`
}

/**
 * The company's Google sign-ins: who each is, who made it, how many accounts it
 * reaches, and whether Google still accepts it.
 */
export function AdConnectionList({ connections, action }: AdConnectionListProps) {
  const remove = useRemoveAdConnection()
  const [removing, setRemoving] = useState<AdConnectionDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  function confirmRemove() {
    if (removing === null) return
    setError(null)
    remove.mutate(removing.id, {
      onSuccess: () => setRemoving(null),
      onError: (e) => setError(requestErrorMessage(e)),
    })
  }

  function status(connection: AdConnectionDto) {
    if (connection.failingSince === null) return <Badge tone="success">Working</Badge>
    return <Badge tone="danger">Not working since {instantLabel(connection.failingSince)}</Badge>
  }

  return (
    <>
      <Table heading={connections.length === 1 ? '1 connection' : `${String(connections.length)} connections`} actions={action}>
        <TableHeader>
          <TableHead>Signed in as</TableHead>
          <TableHead>Connected by</TableHead>
          <TableHead>Reaches</TableHead>
          <TableHead>Status</TableHead>
          <TableActionsHead />
        </TableHeader>
        <TableBody>
          {connections.map((connection) => (
            <TableRow key={connection.id}>
              <TableCell className="font-medium text-ink">{connection.email}</TableCell>
              <TableCell>
                <div className="text-ink">{connection.connectedBy ?? 'Someone no longer here'}</div>
                <div className="whitespace-nowrap text-xs text-ink-muted">{instantLabel(connection.connectedAt)}</div>
              </TableCell>
              <TableCell className="whitespace-nowrap">{accountsLabel(connection.accountCount)}</TableCell>
              <TableCell className="whitespace-nowrap">{status(connection)}</TableCell>
              <TableActionsCell>
                <RowActions
                  label={connection.email}
                  actions={[{ label: 'Remove', icon: Trash2, destructive: true, onSelect: () => setRemoving(connection) }]}
                />
              </TableActionsCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
        title="Remove this connection?"
        description={
          <>
            FLOH Venues stops holding {removing?.email ?? 'this'} sign-in. Every account keeps its venue and its numbers;
            accounts only this connection reaches stop updating until someone connects them again.
          </>
        }
        confirmLabel="Remove"
        destructive
        busy={remove.isPending}
        error={error}
        onConfirm={confirmRemove}
      />
    </>
  )
}
